import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { shopsAPI, ordersAPI } from '../services/api';
import { Shop, ShopPayment, Order, OrderItem } from '../types';
import { useAuth } from '../contexts/AuthContext';
import OrderItemsModal from '../components/OrderItemsModal';
import './ShopDetail.css';

const fmt = (n: number) =>
  Number(n).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Ожидает',
  CONFIRMED: 'Подтверждён',
  SHIPPED: 'Отправлен',
  DELIVERED: 'Доставлен',
  CANCELLED: 'Отменён',
  REJECTED: 'Отклонён',
};

const STATUS_COLORS: Record<string, string> = {
  PENDING: '#f59e0b',
  CONFIRMED: '#3b82f6',
  SHIPPED: '#8b5cf6',
  DELIVERED: '#10b981',
  CANCELLED: '#6b7280',
  REJECTED: '#ef4444',
};

const PAYMENT_LABELS: Record<string, string> = {
  CASH: 'Наличные',
  CREDIT: 'В долг',
  ADVANCE: 'Аванс',
};

const ShopDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin, isAgent } = useAuth();

  const [shop, setShop] = useState<Shop | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'orders' | 'payments'>('orders');

  // Orders
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersTotalPages, setOrdersTotalPages] = useState(1);

  // Payments
  const [payments, setPayments] = useState<ShopPayment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payNotes, setPayNotes] = useState('');
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [itemsModal, setItemsModal] = useState<{ orderId: number; items: OrderItem[] } | null>(null);

  const shopId = Number(id);

  useEffect(() => {
    shopsAPI.getOne(shopId)
      .then(setShop)
      .catch(() => navigate('/shops'))
      .finally(() => setLoading(false));
  }, [shopId, navigate]);

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const res = await ordersAPI.getAll({ shopId, page: ordersPage, limit: 15 });
      setOrders(res.data);
      setOrdersTotalPages(res.meta.totalPages);
    } finally {
      setOrdersLoading(false);
    }
  }, [shopId, ordersPage]);

  const fetchPayments = useCallback(async () => {
    setPaymentsLoading(true);
    try {
      const data = await shopsAPI.getPayments(shopId);
      setPayments(data);
    } finally {
      setPaymentsLoading(false);
    }
  }, [shopId]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shop) return;
    setPaySubmitting(true);
    try {
      await shopsAPI.addPayment(shopId, { amount: parseFloat(payAmount), notes: payNotes || undefined });
      setPayAmount('');
      setPayNotes('');
      const [updatedShop, updatedPayments] = await Promise.all([
        shopsAPI.getOne(shopId),
        shopsAPI.getPayments(shopId),
      ]);
      setShop(updatedShop);
      setPayments(updatedPayments);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Ошибка при записи платежа');
    } finally {
      setPaySubmitting(false);
    }
  };

  const debtColor = (s: Shop) => {
    const debt = Number(s.debt);
    const limit = Number(s.creditLimit);
    if (debt <= 0) return '#065f46';
    if (limit > 0 && debt >= limit) return '#991b1b';
    if (limit > 0 && debt >= limit * 0.7) return '#92400e';
    return '#1e40af';
  };

  if (loading) return <div className="sd-loading">Загрузка...</div>;
  if (!shop) return null;

  return (
    <>
    <div className="sd-page">
      {/* Back */}
      <Link to="/shops" className="sd-back">← Магазины</Link>

      {/* Shop header */}
      <div className="sd-header">
        <div className="sd-header-main">
          <div>
            <h1 className="sd-name">{shop.name}</h1>
            {shop.description && <p className="sd-desc">{shop.description}</p>}
            <div className="sd-meta">
              {shop.address && <span>📍 {shop.address}</span>}
              {shop.phone && <span>📞 {shop.phone}</span>}
              {shop.owner && <span>👤 {shop.owner.firstName} {shop.owner.lastName}</span>}
            </div>
          </div>
          <span className={`sd-status ${shop.isActive ? 'active' : 'inactive'}`}>
            {shop.isActive ? 'Активен' : 'Неактивен'}
          </span>
        </div>

        {/* Balance cards */}
        {!isAgent() && (
          <div className="sd-balance-row">
            <div className="sd-balance-card">
              <span className="sd-balance-label">Долг</span>
              <span className="sd-balance-value" style={{ color: debtColor(shop) }}>{fmt(shop.debt)} сом</span>
            </div>
            <div className="sd-balance-card">
              <span className="sd-balance-label">Аванс</span>
              <span className="sd-balance-value" style={{ color: Number(shop.advance) > 0 ? '#065f46' : '#6b7280' }}>
                {fmt(shop.advance)} сом
              </span>
            </div>
            <div className="sd-balance-card">
              <span className="sd-balance-label">Лимит</span>
              <span className="sd-balance-value">
                {Number(shop.creditLimit) === 0 ? '∞' : `${fmt(shop.creditLimit)} сом`}
              </span>
            </div>
            <div className="sd-balance-card">
              <span className="sd-balance-label">Срок долга</span>
              <span className="sd-balance-value">
                {Number(shop.maxDebtDays) === 0 ? '∞' : `${shop.maxDebtDays} дн.`}
              </span>
            </div>
          </div>
        )}

        {/* Credit bar */}
        {!isAgent() && Number(shop.creditLimit) > 0 && Number(shop.debt) > 0 && (
          <div className="sd-credit-bar-wrap">
            <div
              className="sd-credit-bar"
              style={{
                width: `${Math.min(100, (Number(shop.debt) / Number(shop.creditLimit)) * 100)}%`,
                backgroundColor: debtColor(shop),
              }}
            />
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="sd-tabs">
        <button className={`sd-tab${tab === 'orders' ? ' active' : ''}`} onClick={() => setTab('orders')}>
          История заказов
        </button>
        <button className={`sd-tab${tab === 'payments' ? ' active' : ''}`} onClick={() => setTab('payments')}>
          История платежей
        </button>
      </div>

      {/* Orders tab */}
      {tab === 'orders' && (
        <div className="sd-section">
          {ordersLoading ? (
            <div className="sd-empty">Загрузка...</div>
          ) : orders.length === 0 ? (
            <div className="sd-empty">Заказов нет</div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="sd-table-wrap sd-desktop-table">
                <table className="sd-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Статус</th>
                      <th>Тип оплаты</th>
                      <th>Сумма</th>
                      <th>Агент</th>
                      <th>Дата</th>
                      <th>Товары</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <React.Fragment key={o.id}>
                        <tr>
                          <td className="sd-td-id">#{o.id}</td>
                          <td>
                            <span className="sd-badge" style={{
                              background: STATUS_COLORS[o.status] + '22',
                              color: STATUS_COLORS[o.status],
                            }}>
                              {STATUS_LABELS[o.status] ?? o.status}
                            </span>
                          </td>
                          <td>{PAYMENT_LABELS[o.paymentType] ?? o.paymentType}</td>
                          <td className="sd-td-amount">{fmt(o.totalAmount)} сом</td>
                          <td>{o.agent ? `${o.agent.firstName} ${o.agent.lastName ?? ''}` : '—'}</td>
                          <td className="sd-td-date">{new Date(o.createdAt).toLocaleDateString('ru-RU')}</td>
                          <td>
                            {o.items && o.items.length > 0 ? (
                              <button className="btn-items-link" onClick={() => setItemsModal({ orderId: o.id, items: o.items! })}>
                                {o.items.length} поз.
                              </button>
                            ) : '—'}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile orders list */}
              <div className="sd-mobile-orders-list">
                {orders.map((o) => (
                  <div key={o.id} className="mobile-order-card">
                    <div className="mobile-card-head">
                      <span className="mobile-order-id">#{o.id}</span>
                      <span className="sd-badge" style={{
                        background: STATUS_COLORS[o.status] + '22',
                        color: STATUS_COLORS[o.status],
                      }}>
                        {STATUS_LABELS[o.status] ?? o.status}
                      </span>
                    </div>

                    <div className="mobile-card-details">
                      <div className="mobile-detail-row">
                        <span className="mobile-detail-label">Оплата:</span>
                        <span className="mobile-detail-val">{PAYMENT_LABELS[o.paymentType] ?? o.paymentType}</span>
                      </div>
                      <div className="mobile-detail-row">
                        <span className="mobile-detail-label">Агент:</span>
                        <span className="mobile-detail-val">{o.agent ? `${o.agent.firstName} ${o.agent.lastName ?? ''}` : '—'}</span>
                      </div>
                      <div className="mobile-detail-row">
                        <span className="mobile-detail-label">Товары:</span>
                        <span className="mobile-detail-val">
                          {o.items && o.items.length > 0 ? (
                            <button className="btn-items-link" onClick={() => setItemsModal({ orderId: o.id, items: o.items! })}>
                              🔍 {o.items.length} поз.
                            </button>
                          ) : '—'}
                        </span>
                      </div>
                      <div className="mobile-detail-row">
                        <span className="mobile-detail-label">Дата:</span>
                        <span className="mobile-detail-val">{new Date(o.createdAt).toLocaleDateString('ru-RU')}</span>
                      </div>
                    </div>

                    <div className="mobile-card-foot">
                      <span className="mobile-amount-label">Сумма:</span>
                      <span className="mobile-amount-val">{fmt(o.totalAmount)} сом</span>
                    </div>
                  </div>
                ))}
              </div>

              {ordersTotalPages > 1 && (
                <div className="sd-pagination">
                  <button disabled={ordersPage === 1} onClick={() => setOrdersPage((p) => p - 1)} className="sd-page-btn">← Назад</button>
                  <span>Страница {ordersPage} из {ordersTotalPages}</span>
                  <button disabled={ordersPage === ordersTotalPages} onClick={() => setOrdersPage((p) => p + 1)} className="sd-page-btn">Вперёд →</button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Payments tab */}
      {tab === 'payments' && (
        <div className="sd-section">
          {isAdmin() && (
            <form onSubmit={handleAddPayment} className="sd-pay-form">
              <h3>Записать оплату</h3>
              <div className="sd-pay-row">
                <div className="sd-pay-field">
                  <label>Сумма (сом) *</label>
                  <input type="number" min="0.01" step="0.01" required value={payAmount}
                    onChange={(e) => setPayAmount(e.target.value)} placeholder="0.00" />
                </div>
                <div className="sd-pay-field sd-pay-field-wide">
                  <label>Примечание</label>
                  <input type="text" value={payNotes}
                    onChange={(e) => setPayNotes(e.target.value)} placeholder="Наличные, перевод..." />
                </div>
                <button type="submit" className="sd-btn-primary" disabled={paySubmitting}>
                  {paySubmitting ? '...' : 'Записать'}
                </button>
              </div>
            </form>
          )}

          {paymentsLoading ? (
            <div className="sd-empty">Загрузка...</div>
          ) : payments.length === 0 ? (
            <div className="sd-empty">Платежей нет</div>
          ) : (
            <>
              <div className="sd-table-wrap sd-desktop-table">
                <table className="sd-table">
                  <thead>
                    <tr>
                      <th>Дата</th>
                      <th>Сумма</th>
                      <th>Примечание</th>
                      <th>Кто записал</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.id}>
                        <td className="sd-td-date">{new Date(p.createdAt).toLocaleDateString('ru-RU')}</td>
                        <td className="sd-td-amount" style={{ color: '#065f46' }}>+{fmt(p.amount)} сом</td>
                        <td>{p.notes || '—'}</td>
                        <td>{p.createdBy ? `${p.createdBy.firstName} ${p.createdBy.lastName ?? ''}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="sd-mobile-payments-list">
                {payments.map((p) => (
                  <div key={p.id} className="mobile-payment-card">
                    <div className="mobile-card-head">
                      <span className="mobile-payment-amount">+{fmt(p.amount)} сом</span>
                      <span className="mobile-payment-date">{new Date(p.createdAt).toLocaleDateString('ru-RU')}</span>
                    </div>
                    {p.notes && (
                      <div className="mobile-payment-note">
                        📝 {p.notes}
                      </div>
                    )}
                    <div className="mobile-payment-author">
                      Записал: {p.createdBy ? `${p.createdBy.firstName} ${p.createdBy.lastName ?? ''}` : '—'}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>

    {itemsModal && (
      <OrderItemsModal
        orderId={itemsModal.orderId}
        items={itemsModal.items}
        onClose={() => setItemsModal(null)}
      />
    )}
    </>
  );
};

export default ShopDetail;
